/**
 * 대화 공유(/share/:id) 요청·화면에서 쓰는 순수 함수.
 * 생성 파일은 file_id 만 보내고 이름·형식은 서버가 소유 확인 뒤 채운다. 올린 원본은 이름만 보낸다.
 */

const MAX_ATTACHMENT_NAMES = 10;

/** 이 사용자 메시지에서 새로 올린 파일 이름(앞선 턴에서 이어 쓰는 파일은 빼고). */
export const newlyAttachedFileNames = (message) => {
  const ids = Array.isArray(message?.attachedFileIds) ? message.attachedFileIds : [];
  const names = Array.isArray(message?.attachedFileNames) ? message.attachedFileNames : [];
  const fresh = new Set(Array.isArray(message?.newlyAttachedFileIds) ? message.newlyAttachedFileIds : []);
  if (fresh.size === 0) return [];
  const result = [];
  ids.forEach((id, index) => {
    const name = String(names[index] || '').trim();
    if (fresh.has(id) && name && !result.includes(name)) result.push(name);
  });
  return result.slice(0, MAX_ATTACHMENT_NAMES);
};

/** 공유할 생성 파일 id(완성된 파일만). */
export const shareableArtifactIds = (message) => (
  (Array.isArray(message?.artifacts) ? message.artifacts : [])
    .filter((artifact) => artifact && artifact.file_id && !artifact.pending && !artifact.error)
    .map((artifact) => ({ file_id: String(artifact.file_id) }))
);

/** 대화 → 공유 요청 메시지. toShareText(msg) 는 답변 본문을 공유 글로 바꾼다. */
export const buildShareMessages = (messages, toShareText) => (
  (Array.isArray(messages) ? messages : []).map((msg) => {
    const item = {
      role: msg.role,
      content: msg.role === 'assistant' ? toShareText(msg) : msg.content,
      timestamp: msg.timestamp,
      category: msg.category,
    };
    if (msg.role === 'assistant') {
      const artifacts = shareableArtifactIds(msg);
      if (artifacts.length > 0) item.artifacts = artifacts;
    } else {
      const names = newlyAttachedFileNames(msg);
      if (names.length > 0) item.attachment_names = names;
    }
    return item;
  })
);

const FORMAT_LABELS = {
  hwpx: '한글', hwp: '한글', docx: '워드', pdf: 'PDF', xlsx: '엑셀', csv: 'CSV', pptx: 'PPT',
};

export const shareFileFormatLabel = (format) => FORMAT_LABELS[String(format || '').toLowerCase()] || '파일';

export const formatShareFileSize = (bytes) => {
  const size = Number(bytes) || 0;
  if (size <= 0) return '';
  if (size < 1024) return `${size}B`;
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))}KB`;
  return `${(size / (1024 * 1024)).toFixed(1)}MB`;
};

/** 공유 화면 "나도 만들기" 주소. 같은 메뉴와 첫 질문을 입력창에 채운다. */
export const buildShareTryUrl = (sharedData) => {
  const params = new URLSearchParams();
  const category = String(sharedData?.category || '').trim();
  if (/^[a-z_]{2,40}$/.test(category)) params.set('category', category);
  const firstUser = (sharedData?.messages || []).find((msg) => msg?.role === 'user' && String(msg.content || '').trim());
  if (firstUser) params.set('question', String(firstUser.content).trim().slice(0, 300));
  params.set('utm_source', 'chat_share');
  params.set('utm_medium', 'share_page');
  params.set('utm_campaign', 'chat_share_try');
  return `/chat?${params.toString()}`;
};

/** 공유에 담긴 파일 수 요약(계측용, 이름 없이). */
export const summarizeSharedFiles = (messages) => {
  let generated = 0;
  let attached = 0;
  (Array.isArray(messages) ? messages : []).forEach((msg) => {
    generated += Array.isArray(msg?.artifacts) ? msg.artifacts.length : 0;
    attached += Array.isArray(msg?.attachment_names) ? msg.attachment_names.length : 0;
  });
  return { generated, attached };
};
